import { HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { NbAuthJWTToken, NbAuthResult, NbAuthService } from "@nebular/auth";
import { Observable, defer, of } from "rxjs";
import { catchError, finalize, shareReplay } from "rxjs/operators";

const STRATEGY_EMAIL = "email";

@Injectable({
  providedIn: 'root'
})
export class RequestTokenService {
  private token: NbAuthJWTToken = null;
  private renovacaoEmCurso: Observable<NbAuthResult> | null = null;

  constructor(private authService: NbAuthService) {
    this.authService.onTokenChange()
    .subscribe((token: NbAuthJWTToken) => {
      if (token && token.isValid()) {
        this.token = token;
      } else {
        this.token = null;
      }
    });
   }

  createAuthorizationHeader(): HttpHeaders {
    
    if (!this.token) {      
      // Tentar obter token atual do authService
      this.authService.getToken().subscribe((token: NbAuthJWTToken) => {
        if (token && token.isValid()) {
          this.token = token;
        } else {
          return new HttpHeaders();
        }
      });
    }

    if (!this.token || !this.token.isValid()) {
      return new HttpHeaders();
    }

    
    const raw = this.token.getValue();
    // API (JwtBearer) exige o esquema "Bearer "; valor armazenado pelo Nebular é só o JWT
    const authorization = raw.startsWith('Bearer ') ? raw : `Bearer ${raw}`;
    return new HttpHeaders().set('Authorization', authorization);
  }

  /**
   * O JWT vencido continua em localStorage (`auth_app_token`).
   * A renovação usa esse valor, mesmo quando o Nebular já o considera inválido.
   */
  renovarToken(): Observable<NbAuthResult> {
    if (!this.renovacaoEmCurso) {
      this.renovacaoEmCurso = defer(() => this.pedirNovoToken()).pipe(
        finalize(() => {
          this.renovacaoEmCurso = null;
        }),
        shareReplay(1),
      );
    }
    return this.renovacaoEmCurso;
  }

  /** 401 com token ainda válido é falta de permissão, não sessão vencida. */
  autorizacaoExpirada(authorization: string | null): boolean {
    if (authorization == null || authorization.trim() === "") {
      return true;
    }
    const jwt = authorization.replace(/^Bearer\s+/i, "").trim();
    const expiraEm = this.lerExpiracao(jwt);
    if (!expiraEm) {
      return true;
    }
    return expiraEm.getTime() <= Date.now();
  }

  private pedirNovoToken(): Observable<NbAuthResult> {
    const jwt = this.lerJwtArmazenado();
    if (!jwt) {
      return of(new NbAuthResult(false, undefined, null, ["Sem token armazenado"], []));
    }
    const login = this.obterLoginParaRefresh(jwt);
    if (!login) {
      return of(
        new NbAuthResult(
          false,
          undefined,
          null,
          ["Não foi possível identificar o usuário para renovar a sessão."],
          [],
        ),
      );
    }
    return this.authService.refreshToken(STRATEGY_EMAIL, { login, token: jwt }).pipe(
      catchError(() =>
        of(new NbAuthResult(false, undefined, null, ["Não foi possível renovar a sessão."], [])),
      ),
    );
  }

  private lerJwtArmazenado(): string | null {
    let bruto: string | null = null;
    try {
      bruto = localStorage.getItem("auth_app_token");
    } catch {
      return null;
    }
    if (bruto == null || bruto.trim() === "") {
      return null;
    }
    let valor = bruto.trim();
    try {
      const pacote = JSON.parse(bruto);
      if (pacote && typeof pacote.value === "string" && pacote.value.trim() !== "") {
        valor = pacote.value.trim();
      }
    } catch {
      // valor cru, sem o pacote do Nebular
    }
    const jwt = valor.replace(/^Bearer\s+/i, "").trim();
    return jwt || null;
  }

  private lerExpiracao(jwt: string): Date | null {
    try {
      const partes = jwt.split(".");
      if (partes.length < 2) {
        return null;
      }
      const base64 = partes[1].replace(/-/g, "+").replace(/_/g, "/");
      const payload = JSON.parse(atob(base64));
      if (payload?.exp == null) {
        return null;
      }
      const data = new Date(0);
      data.setUTCSeconds(Number(payload.exp));
      return Number.isNaN(data.getTime()) ? null : data;
    } catch {
      return null;
    }
  }

  private obterLoginParaRefresh(jwt: string): string | null {
    const fromSession = sessionStorage.getItem("emailUsuario");
    if (fromSession && fromSession.trim()) {
      return fromSession.trim();
    }
    try {
      const parts = jwt.split(".");
      if (parts.length < 2) {
        return null;
      }
      const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      const payload = JSON.parse(atob(base64));
      const email = payload.emailUsuario ?? payload.email;
      return typeof email === "string" ? email.trim() : null;
    } catch {
      return null;
    }
  }
}
